/**
 * 궤도 카메라 — 요 자유회전, 피치 제한, 줌 클램프, 우클릭 팬, 핀치.
 * 월드/필지 내부 씬별로 카메라 상태(camW/camP) 분리. 프로토타입 이식.
 */
import * as THREE from 'three';

type Mode = 'world' | 'parcel';
interface CamState {
  yaw: number;
  pitch: number;
  dist: number;
  tx: number;
  tz: number;
}

export class OrbitCamera {
  readonly camW: CamState = { yaw: 0.4, pitch: 0.95, dist: 170, tx: 0, tz: 60 };
  readonly camP: CamState = { yaw: 0.4, pitch: 0.95, dist: 52, tx: 0, tz: 4 };

  private dragging = 0;
  private px = 0;
  private py = 0;
  private pinch0 = 0;

  constructor(
    private dom: HTMLElement,
    private getMode: () => Mode,
  ) {
    this.attach();
  }

  private cur(): CamState {
    return this.getMode() === 'world' ? this.camW : this.camP;
  }

  /** 필지 진입 시 내부 카메라 초기화. */
  resetParcel(): void {
    this.camP.dist = 52;
    this.camP.yaw = 0.4;
    this.camP.pitch = 0.95;
    this.camP.tx = 0;
    this.camP.tz = 4;
  }

  private attach(): void {
    this.dom.addEventListener('pointerdown', (e) => {
      this.dragging = e.button === 2 ? 2 : 1;
      this.px = e.clientX;
      this.py = e.clientY;
    });
    window.addEventListener('pointerup', () => (this.dragging = 0));
    window.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      const cam = this.cur();
      const dx = e.clientX - this.px;
      const dy = e.clientY - this.py;
      this.px = e.clientX;
      this.py = e.clientY;
      if (this.dragging === 1) {
        cam.yaw -= dx * 0.005;
        cam.pitch = Math.max(0.62, Math.min(1.2, cam.pitch + dy * 0.003));
      } else {
        const s = cam.dist * 0.0016;
        cam.tx -= (Math.cos(cam.yaw) * dx + Math.sin(cam.yaw) * dy) * s;
        cam.tz -= (-Math.sin(cam.yaw) * dx + Math.cos(cam.yaw) * dy) * s;
      }
    });
    this.dom.addEventListener(
      'wheel',
      (e) => {
        const cam = this.cur();
        const world = this.getMode() === 'world';
        cam.dist = Math.max(
          world ? 60 : 26,
          Math.min(world ? 340 : 110, cam.dist + e.deltaY * 0.12),
        );
      },
      { passive: true },
    );
    this.dom.addEventListener('contextmenu', (e) => e.preventDefault());
    this.dom.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 2) {
          const cam = this.cur();
          const a = e.touches[0]!;
          const b = e.touches[1]!;
          const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
          if (this.pinch0) cam.dist = Math.max(26, Math.min(340, cam.dist - (d - this.pinch0) * 0.4));
          this.pinch0 = d;
        }
      },
      { passive: true },
    );
    this.dom.addEventListener('touchend', () => (this.pinch0 = 0));
  }

  apply(camera: THREE.PerspectiveCamera): void {
    const cam = this.cur();
    const cy = Math.sin(cam.pitch);
    const ch = Math.cos(cam.pitch);
    camera.position.set(
      cam.tx + Math.sin(cam.yaw) * ch * cam.dist,
      cy * cam.dist,
      cam.tz + Math.cos(cam.yaw) * ch * cam.dist,
    );
    camera.lookAt(cam.tx, 0, cam.tz);
  }
}
